import {
	asyncWithLDProvider,
	useFlags,
	useLDClient,
} from 'launchdarkly-react-client-sdk';
import localLdOverrides from 'local-ld-overrides';
import { UUID_COOKIE_NAME } from 'stash/constants/session';
import { LAUNCH_DARKLY_CLIENT_IDS } from 'stash/constants/ab-testing';
import { getCookie } from 'stash/utils/cookies';
import { useSelector } from 'react-redux';
import { userSelector } from 'stash/selectors/user';
import { useEffect, useRef } from 'react';
import { datadogRum } from '@datadog/browser-rum';

function createContext(uuid?: string) {
	const context: { kind: 'user'; key?: string; anonymous?: boolean } = {
		kind: 'user',
	};

	if (uuid) {
		context.key = uuid;
	} else {
		context.anonymous = true;
	}
	return context;
}

function coerceOverrideVariant(
	value: string | boolean | number
): boolean | string | undefined {
	const normalized = typeof value === 'string' ? value : String(value);

	switch (normalized) {
		case 'default':
			return undefined;
		case 'false':
			return false;
		case 'true':
			return true;
		default:
			return normalized;
	}
}

/**
 * Experiment keys of current Launch Darkly ab tests.
 */
export const EXPERIMENTS = {
	IS_AUTO_STASH_PARITY_ENABLED: 'is-auto-stash-parity-enabled',
	IS_NEW_IDV_CONFIRMATION_SCREEN_ENABLED:
		'web-revised-copy-on-doc-confirmation-2025-03-12',
	REFERRAL_CAMPAIGN_TAG: 'web-new-referral-variant',
	IS_STOCKBACK_SHARE_TILE_DEPOSIT_ENABLED:
		'web-2025-07-10-stockback-share-tile-deposit-enabled',
	IS_SHOW_TAX_DOC_BANNER_ENABLED: 'web-show-tax-doc-banner',
	IS_SECONDARY_IRA_OPENING_ENABLED: 'web-secondary-ira-opening-enabled',
	WEB_CAPITALIZE_ROLLOVER_ENABLED: 'web-capitalize-rollover-enabled',
	IS_STASH_STATS_ENABLED: 'web-wrapped-banner',
	IS_FULLY_PAID_SECURITIES_LENDING_CONFIRMATIONS_ENABLED:
		'web-securities-lending-confirmations-enabled',
	WEB_CANCEL_AND_DOWNGRADE_FLOW_CHANGES: 'web-cancel-and-downgrade-flow-changes',
	WEB_CLOSURE_SUMMARY_SCREEN_ENABLED: 'web-closure-summary-screen-enabled',
	WEB_DOWNGRADE_NEXT_STEPS_V2_ENABLED: 'web-downgrade-next-steps-v2-enabled',
	IS_MANAGED_IRA_CONVERSION_ENABLED: 'web-managed-ira-conversion-enabled',
	IS_MARKET_INSIGHTS_REPORT_ENABLED: 'web-market-insights-report-enabled',
	IS_NEXT_BEST_ACTION_ENABLED: 'web-next-best-action-enabled',
	IS_NBA_ADVICE_ENDPOINT_ENABLED: 'web-nba-advice-endpoint-enabled',
	WEB_AI_MONEY_COACH_TAB_ENABLED: 'web-ai-money-coach-tab-enabled',
	WEB_APP_DOWNLOAD_CARD_ENABLED: 'web-app-download-card-enabled',
	IS_NOTIFICATION_CENTER_ENABLED: 'web-notification-center-enabled',
	WEB_INVEST_AN_IDEA_CHAT_ENABLED: 'web-invest-an-idea-chat-enabled',
	WEB_INVEST_AN_IDEA_REDESIGN_ENABLED: 'web-invest-an-idea-redesign-enabled',
	WEB_STOCK_PARTY_IN_APP_ENABLED: 'web-stock-party-in-app-enabled',
} as const;

export const getLaunchDarklyProvider = () => {
	const clientSideID =
		LAUNCH_DARKLY_CLIENT_IDS[window.Stash.env as keyof typeof LAUNCH_DARKLY_CLIENT_IDS];

	return asyncWithLDProvider({
		context: createContext(getCookie(UUID_COOKIE_NAME) as string | undefined),
		clientSideID,
		options: {
			streaming: false,
			disableSyncEventPost: true,
			inspectors: [
				{
					type: 'flag-used',
					name: 'dd-inspector',
					method: (key: string, detail: { value: unknown }) => {
						datadogRum.addFeatureFlagEvaluation(key, detail.value);
					},
				},
			],
		},
		reactOptions: {
			useCamelCaseFlagKeys: false,
		},
	});
};

export function ExperimentsContextUpdater() {
	const mountedRef = useRef(false);
	const client = useLDClient();
	const user = useSelector(userSelector);

	useEffect(() => {
		// skip the first render since the provider will make the initial call
		if (mountedRef.current) {
			client?.identify(createContext(user?.uuid));
		}
		mountedRef.current = true;
	}, [user?.uuid, client]);

	return null;
}

export function useCurrentVariant(experiment: string): boolean | string | undefined {
	const flags = (useFlags() as Record<string, boolean | string | undefined>) || undefined;

	const localTestVariant = localStorage.getItem(experiment);
	const hasLocalTestVariant = localTestVariant !== null && localTestVariant !== undefined;

	if (hasLocalTestVariant) {
		return coerceOverrideVariant(localTestVariant);
	}

	if (import.meta.env.DEV && Object.prototype.hasOwnProperty.call(localLdOverrides, experiment)) {
		return coerceOverrideVariant(localLdOverrides[experiment]);
	}

	return flags ? flags[experiment] : undefined;
}

/**
 * Renders DOM elements when in the provided experiment variant.
 */
type ExperimentVariantProps = {
	experiment: string;
	variant: boolean | string;
	defaultVariant?: boolean;
	children: React.ReactNode;
};

export function ExperimentVariant({
	experiment,
	variant,
	defaultVariant,
	children,
}: ExperimentVariantProps) {
	const currentVariant = useCurrentVariant(experiment);

	if (defaultVariant && currentVariant === undefined) {
		return children as JSX.Element;
	}

	return variant === currentVariant ? (children as JSX.Element) : null;
}

/**
 * Returns true if the user is in the provided experiment variant.
 * For pure business logic; useful when not rendering DOM elements.
 */
export function useIsInExperimentVariation(
	experiment: string,
	variant: boolean | string
) {
	const currentVariant = useCurrentVariant(experiment);

	return variant === currentVariant;
}
